import { parseApiError } from './http-error';

export { ApiError, apiErrorText } from './http-error';

// VITE_API_URL is the bare host:port (e.g. http://localhost:3000).
// The backend's global prefix is /api/v1, so we append it here.
const BASE = `${(import.meta.env.VITE_API_URL ?? 'http://localhost:3000').replace(/\/$/, '')}/api/v1`;

// Org ID is fetched at runtime from /mobile/luvelle/config (so no build-time
// VITE_ORG_ID is required). Falls back to the env var for CI / staging use.
let _orgId: string = import.meta.env.VITE_ORG_ID ?? '';

function unwrap<T>(data: any): T {
  // Backend wraps responses as { success, data }. Unwrap when present.
  if (data && typeof data === 'object' && 'data' in data && 'success' in data) {
    return data.data as T;
  }
  return data as T;
}

export async function fetchAndCacheOrgId(): Promise<string> {
  if (_orgId) return _orgId;
  try {
    const res = await fetch(`${BASE}/mobile/luvelle/config`);
    if (res.ok) {
      const json = await res.json();
      const payload = unwrap<{ organizationId?: string }>(json);
      _orgId = payload.organizationId ?? '';
    }
  } catch {
    // fallback to env var or empty — auth calls will fail gracefully
  }
  return _orgId;
}

function getOrgId(): string {
  return _orgId;
}

async function post<T>(path: string, body: unknown, token?: string): Promise<T> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  const res = await fetch(`${BASE}${path}`, {
    method: 'POST',
    headers,
    body: JSON.stringify(body),
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw parseApiError(res.status, json);
  return unwrap<T>(json);
}

async function get<T>(path: string, token: string): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw parseApiError(res.status, json);
  return unwrap<T>(json);
}

export interface Customer {
  id: string;
  fullName: string;
  phone: string;
  email: string | null;
  isNafathVerified: boolean;
  organizationId?: string;
  nafathVerifiedAt?: string | null;
}

export interface RequestOtpResult {
  ok: true;
  devOtp: string | null;
}

export type VerifyOtpResult =
  | { status: 'authenticated'; accessToken: string; customer: Customer }
  | { status: 'needsRegistration'; registerToken: string };

export interface RegisterResult {
  accessToken: string;
  customer: Customer;
}

export const authApi = {
  requestOtp: async (phone: string) => {
    // Ensure org ID is loaded before sending — the AuthContext fetch may
    // not have resolved yet on a direct-to-login navigation.
    const organizationId = await fetchAndCacheOrgId();
    return post<RequestOtpResult>('/mobile/shared/auth/request-otp', {
      phone,
      organizationId,
    });
  },

  verifyOtp: async (phone: string, otp: string) => {
    const organizationId = (await fetchAndCacheOrgId()) || getOrgId();
    return post<VerifyOtpResult>('/mobile/shared/auth/verify-otp', {
      phone,
      otp,
      organizationId,
    });
  },

  register: (registerToken: string, fullName: string, email?: string) =>
    post<RegisterResult>('/mobile/shared/auth/register', {
      registerToken,
      fullName,
      ...(email ? { email } : {}),
    }),

  getMe: (token: string) => get<Customer>('/mobile/shared/auth/me', token),
};
